import { inject, Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { doc, Firestore, increment, updateDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AccountingService {
  private afs: AngularFirestore = inject(AngularFirestore);
  private functions: Functions = inject(Functions);
  private firestore: Firestore = inject(Firestore);

  createId(): string {
    return this.afs.createId();
  }

  /**
   * Calls the Stripe callable cloud function to create a donation session.
   */
  async createDonationCheckoutSession(params: {
    amount: number;
    currency: string;
    mode: 'payment' | 'subscription';
    groupId?: string;
    fundingId?: string;
    isAtChurchBusiness?: boolean;
    successUrl?: string;
    cancelUrl?: string;
  }): Promise<{ url: string; sessionId: string }> {
    const fn = httpsCallable(
      this.functions,
      'payment_createDonationCheckoutSession',
    );
    const response = await fn(params);
    return response.data as { url: string; sessionId: string };
  }

  async createDonationPaymentIntent(params: {
    amount: number;
    currency: string;
    groupId: string;
    fundingId?: string;
    name: string;
    email: string;
  }): Promise<{ clientSecret: string; paymentIntentId: string }> {
    const fn = httpsCallable(
      this.functions,
      'payment_createDonationPaymentIntent',
    );
    const response = await fn(params);
    return response.data as { clientSecret: string; paymentIntentId: string };
  }

  async createDonationSubscription(params: {
    amount: number;
    currency: string;
    groupId: string;
    fundingId?: string;
    setupIntentId: string;
  }): Promise<{ subscriptionId: string; status: string }> {
    const fn = httpsCallable(
      this.functions,
      'payment_createDonationSubscription',
    );
    const response = await fn(params);
    return response.data as { subscriptionId: string; status: string };
  }

  async createStripeConnectAccountLink(params: {
    groupId: string;
    returnUrl: string;
    refreshUrl: string;
  }): Promise<{ url: string }> {
    const fn = httpsCallable(
      this.functions,
      'payment_createConnectAccountLink',
    );
    const response = await fn(params);
    return response.data as { url: string };
  }

  getAllUserPayments(userId: string, startDate?: Date): Observable<any[]> {
    return this.afs
      .collectionGroup('payments', (ref) => {
        let q = ref.where('userId', '==', userId);
        if (startDate) {
          q = q.where('timestamp', '>=', startDate);
        }
        return q.orderBy('timestamp', 'desc');
      })
      .valueChanges({ idField: 'id' });
  }



  /**
   * Returns an observable of the user's payments in the group.
   */
  getPayments(groupId: string, userId: string): Observable<any[]> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('payments', (ref) =>
        ref.where('userId', '==', userId).orderBy('timestamp', 'desc'),
      )
      .valueChanges({ idField: 'id' });
  }

  /**
   * Returns an observable of all payments in the group (for treasurer/admin).
   */
  getAllGroupPayments(groupId: string): Observable<any[]> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('payments', (ref) => ref.orderBy('timestamp', 'desc'))
      .valueChanges({ idField: 'id' });
  }

  /**
   * Returns an observable of all ledger entries for a group.
   */
  getLedger(groupId: string): Observable<any[]> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('ledger', (ref) => ref.orderBy('date', 'desc'))
      .valueChanges({ idField: 'id' });
  }

  /**
   * Adds a manual transaction ledger entry.
   */
  async addLedgerEntry(groupId: string, entry: any): Promise<any> {
    const id = this.afs.createId();
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('ledger')
      .doc(id)
      .set({
        ...entry,
        id,
        created: new Date(),
      });
  }

  /**
   * Returns an observable of funding requests for a group.
   */
  getFunding(groupId: string): Observable<any[]> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('funding', (ref) => ref.orderBy('created', 'desc'))
      .valueChanges({ idField: 'id' });
  }

  /**
   * Adds a new funding request.
   */
  async addFunding(groupId: string, funding: any): Promise<any> {
    const id = this.afs.createId();
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('funding')
      .doc(id)
      .set({
        ...funding,
        id,
        created: new Date(),
      });
  }

  /**
   * Returns an observable of expense reports for a group.
   */
  getExpenseReports(groupId: string): Observable<any[]> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('expense_reports', (ref) => ref.orderBy('created', 'desc'))
      .valueChanges({ idField: 'id' });
  }

  /**
   * Saves an expense report (as draft or published).
   */
  async saveExpenseReport(groupId: string, report: any): Promise<any> {
    const id = report.id || this.afs.createId();
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('expense_reports')
      .doc(id)
      .set(
        {
          ...report,
          id,
          modified: new Date(),
          created: report.created ? new Date(report.created) : new Date(),
        },
        { merge: true },
      );
  }

  /**
   * Atomically publishes an expense report and writes all its expenses to the ledger.
   */
  async publishExpenseReport(
    groupId: string,
    reportId: string,
    expenses: any[],
  ): Promise<any> {
    const batch = this.afs.firestore.batch();

    // 1. Update the status of the report to 'published'
    const reportRef = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('expense_reports')
      .doc(reportId).ref;
    batch.update(reportRef, {
      status: 'published',
      modified: new Date(),
    });

    // 2. Add each expense as an entry in the ledger
    const ledgerCollRef = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('ledger');
    expenses.forEach((exp) => {
      const entryId = this.afs.createId();
      const entryRef = ledgerCollRef.doc(entryId).ref;
      batch.set(entryRef, {
        id: entryId,
        amount: exp.amount,
        currency: exp.currency || 'usd',
        date: exp.date, // format YYYY-MM-DD
        description: exp.description,
        type: 'expense',
        payerName: exp.from || '',
        paidTo: exp.to || '',
        recordedBy: exp.recordedBy || '',
        tags: exp.tags
          ? exp.tags
              .split(',')
              .map((t: string) => t.trim().toLowerCase())
              .filter(Boolean)
          : [],
        receiptUrl: exp.receiptUrl || '',
        reportId: reportId, // link to the parent report
        created: new Date(),
      });
    });

    return batch.commit();
  }

  async trackUsedTokens(
    groupId: string,
    usage: {
      promptTokens: number;
      completionTokens: number;
      totalTokens: number;
    },
  ): Promise<void> {
    const fn = httpsCallable(this.functions, 'accounting_trackUsedTokens', {
      limitedUseAppCheckTokens: true,
    });
    await fn({ groupId, usage });
  }

  /**
   * Returns an observable of the precalculated group financial summary.
   */
  getFinancialSummary(groupId: string): Observable<any> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('financials')
      .doc('summary')
      .valueChanges();
  }

  /**
   * Calls the cloud function to recalculate the group's financial summary.
   */
  async recalculateFinancialSummary(groupId: string): Promise<any> {
    const fn = httpsCallable(
      this.functions,
      'payment_recalculateGroupFinancialSummary',
    );
    const response = await fn({ groupId });
    return response.data;
  }
}
